import Button from 'components/button/Button';
import FormElement from 'components/formElement/FormElement';
import Input from 'components/input/Input';
import { isEmail } from 'modules/common';
import { useRouter } from 'next/router';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';

import sendEmail from '../../modules/sendEmail';
import { ButtonWrapper, TextareaStyled } from './ContactForm.styles';

const errorMessages = {
  email: {
    required: 'Please enter your email',
    validate: 'Please enter a valid email',
  },
  companyName: {
    required: 'Please enter the name of your company',
  },
  message: {
    required: 'Please enter a message',
  },
};

interface ContactFormProps {
  companyName?: string;
  email?: string;
}

interface FormData {
  companyName: string;
  email: string;
  message: string;
}

const ContactForm = ({ companyName, email }: ContactFormProps): JSX.Element => {
  const router = useRouter();
  const {
    register,
    formState: { errors },
    handleSubmit,
    control,
  } = useForm<FormData>({
    mode: 'onSubmit',
  });

  const onSubmit = async (data: FormData) => {
    const response = await sendEmail(
      data.companyName,
      data.email.toLowerCase().trim(),
      data.message
    );
    if (response.errors) {
      toast.error(response.errors[0].message);
    } else {
      router.push('/?message=contactUsSuccess');
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <FormElement
        label="Company Name*"
        name="companyName"
        error={
          errors.companyName &&
          errorMessages.companyName[errors.companyName.type]
        }
      >
        <Input
          type="text"
          name="companyName"
          inputMaxLength="50"
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...register('companyName', {
            required: errorMessages.companyName.required,
          })}
        />
      </FormElement>

      <FormElement
        label="Email*"
        name="email"
        error={errors.email && errorMessages.email[errors.email.type]}
      >
        <Input
          type="text"
          name="email"
          inputMaxLength="70"
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...register('email', {
            required: errorMessages.email.required,
            validate: (value: string) =>
              isEmail(value.trim()) || errorMessages.email.validate,
          })}
          defaultValue={email || ''}
        />
      </FormElement>

      <FormElement
        label="Message*"
        name="message"
        error={errors.message && errorMessages.message[errors.message.type]}
      >
        <Controller
          name="message"
          control={control}
          rules={{
            required: errorMessages.message.required,
          }}
          render={({ field }) => (
            <TextareaStyled
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...field}
              rows={5}
              maxLength={2000}
              placeholder="Please enter your message"
            />
          )}
        />
      </FormElement>

      <ButtonWrapper>
        <Button kind="primary" text="Send" type="submit" />
      </ButtonWrapper>
    </form>
  );
};

export default ContactForm;
